/**
 * btcDca.js — 업비트 BTC 현물 보유분을 "BTC 적립(DCA)" 지표로 환산한다 (DAS-175).
 *
 * BTC 적립과 델타전략은 **같은 업비트 계좌**에서 돌아간다. 계좌로는 나눌 수 없으므로
 * 보유 BTC 현물 평가액(DAS-172에서 이미 리밸런싱 차감용으로 분리해 둔 값)을 경계로 삼는다.
 * 즉 BTC 현물 = 적립분, 나머지(알트 현물 + 선물 마진) = 델타 운용액이다.
 *
 * ⚠️ `balance`는 수량, `current_amount`가 평가액이다 — utils/upbitPosition.js 참조.
 */
import {
    fiatPositionPrincipalKRW,
    fiatPositionQty,
    fiatPositionValueKRW,
} from "./upbitPosition";

export const BTC_CURRENCY = "BTC";

const EMPTY = {
    hasHolding: false,
    qty: 0,
    value: 0,
    principal: 0,
    pnl: 0,
    pnlPct: null,
    avgBuyPrice: null,
    currentPrice: null,
};

/**
 * balanceInfo 전체에서 BTC 현물을 합산해 적립 지표를 만든다.
 *
 * @param {Array}  balanceInfo            /balance 응답 배열
 * @param {Object} [options]
 * @param {string[]} [options.excludeStrategies] 합산에서 뺄 전략명 (ListingSnipe는 같은 키를 중복 노출한다)
 * @returns {{hasHolding: boolean, qty: number, value: number, principal: number,
 *            pnl: number, pnlPct: number|null, avgBuyPrice: number|null, currentPrice: number|null}}
 */
export function buildBtcDcaSummary(balanceInfo, options = {}) {
    const { excludeStrategies = [] } = options;
    const safe = Array.isArray(balanceInfo) ? balanceInfo : [];

    let qty = 0;
    let value = 0;
    let principal = 0;
    // 현재가는 포지션이 알려주면 그대로 쓰고(여러 전략이 같은 계좌를 보므로 값이 같다),
    // 없으면 평가액/수량으로 역산한다.
    let reportedPrice = null;

    safe.forEach((info) => {
        const strategyName = info?.strategy_name || "default";
        if (excludeStrategies.includes(strategyName)) return;
        (info?.fiat_balance?.positions || []).forEach((p) => {
            if ((p?.currency || "").toUpperCase() !== BTC_CURRENCY) return;
            qty += fiatPositionQty(p);
            value += fiatPositionValueKRW(p);
            principal += fiatPositionPrincipalKRW(p);
            const price = parseFloat(p?.current_price);
            if (reportedPrice == null && Number.isFinite(price) && price > 0) reportedPrice = price;
        });
    });

    if (qty <= 0 || value <= 0) return { ...EMPTY };

    const pnl = value - principal;
    return {
        hasHolding: true,
        qty,
        value,
        principal,
        pnl,
        // 원금이 0이면(평단 미제공) 수익률은 계산하지 않는다 — 0으로 두면 손익 0%로 오독된다.
        pnlPct: principal > 0 ? (pnl / principal) * 100 : null,
        avgBuyPrice: principal > 0 ? principal / qty : null,
        currentPrice: reportedPrice != null ? reportedPrice : value / qty,
    };
}
